import type { AdClickInputDto } from '@cineamo/api/models/advertisement/AdClickInputDto.types';
import type { AdImpressionInputDto } from '@cineamo/api/models/advertisement/AdImpressionInputDto.types';
import type { AdPlacement } from '@cineamo/api/models/advertisement/AdPlacement.enum';

import type { Client } from '@cineamo/api/constants/Client.enum';

import type { AdvertisementPublicData } from '@cineamo/api/helpers/advertisement-helper';
import type { GetAdvertisementDisplayParameter } from '@cineamo/api/resources/advertisement/advertisement-api';

export type AdvertisementTrackEvent = 'ad_impression' | 'ad_click' | 'ad_close';

// PostHog display names for `onTrack` events — shared so every host app's PostHog funnel sees
// identical event names regardless of which app served the ad. Pulse keeps the raw event keys.
export const ADVERTISEMENT_TRACK_EVENT_NAME: Record<AdvertisementTrackEvent, string> = {
    ad_impression: 'Advertisement Impression',
    ad_click: 'Advertisement Click',
    ad_close: 'Advertisement Closed'
};

export type AdvertisementTrackPayload = {
    advertisementId: AdvertisementPublicData['id'];
    placement: AdPlacement;
    // The film behind the ad, when there is one. Pulse needs it to attribute the event to a movie:
    // `InterestTrackHandler` resolves a `content` entity to its movie server-side (CIN-14405), and
    // `interest_event.movie_id` is a generated column that stays NULL without it — which hides the
    // event from every distributor query and all four aggregate tables.
    // Undefined for external ads (`adCategory !== 'film'`), which have no content at all.
    contentId?: AdvertisementPublicData['contentId'];
    // The served A/B variant, so the `Movie › Ad › Version` analytics tree has its Version level.
    variantId?: AdvertisementPublicData['variantId'];
};

// Everything an app-specific host needs to inject into `AdvertisementProvider` — the composable
// owns the display query, variant-sticky visitor id, cookie-based close state and impression/click
// posting; the host only supplies fetchers, channel identity and an analytics hook.
export type AdvertisementDisplayAdapter = {
    // Server action (or plain fetcher) hitting `GET /advertisements/display` — the host owns auth.
    fetchAdvertisement: (params: GetAdvertisementDisplayParameter) => Promise<AdvertisementPublicData | null>;
    // Server actions (or plain fetchers) hitting the impression/click endpoints.
    postImpression: (advertisementId: AdvertisementPublicData['id'], data: AdImpressionInputDto) => Promise<unknown>;
    postClick: (advertisementId: AdvertisementPublicData['id'], data: AdClickInputDto) => Promise<unknown>;
    // Which channel this app represents on the display request + tracking payloads.
    displayLocation: Client;
    // Omit for channels without a cinema relation (e.g. the Cineamo platform's own ads).
    cinemaId?: number;
    // White-label scoping for the display request.
    tenantId?: string;
    // Attributed to impressions/clicks, e.g. 'homepage' — the host decides per page.
    referrerPage?: string;
    // Resolved by the host however it likes (own API route, header, etc.) — the composable never fetches it.
    ipAddress?: string;
    // Gate the display query beyond "visitor id resolved" — e.g. wait for a cinema to be selected.
    enabled?: boolean;
    // Fired alongside the impression/click POST so the host can forward to its own analytics stack(s).
    onTrack?: (event: AdvertisementTrackEvent, payload: AdvertisementTrackPayload) => void;
};
